/**
 * Halte einer Mehr-Halte-Fahrt (Zwischenstopps).
 *
 * Konzept: `docs/general/zwischenstopps_konzept.md` (Mobile-Repo).
 * Gegenstück zu `lib/models/ride_stop.dart` in der App — beide Seiten müssen
 * dieselbe Form schreiben, sonst sieht das Portal andere Halte als der Fahrer.
 *
 * **Leitentscheidung (§2.1):** `stops` ist additiv. `destination`,
 * `destinationLatitude` und `destinationLongitude` bleiben erhalten und
 * spiegeln IMMER den letzten Halt. Jede Stelle, die `stops` nicht kennt, sieht
 * weiterhin eine gültige Fahrt von A nach Z. Fehlendes oder leeres `stops` =
 * heutiges Verhalten, unverändert.
 */

/** Maximale Anzahl Halte inklusive Endziel (§10.1, Phase 1). */
export const MAX_STOPS = 5;

/** Zwischenstopps zusätzlich zum Endziel. */
export const MAX_INTERMEDIATE_STOPS = MAX_STOPS - 1;

export const STOP_TYPE_DROPOFF = 'dropoff';
export const STOP_STATUS_PENDING = 'pending';
export const STOP_STATUS_DONE = 'done';
export const STOP_STATUS_SKIPPED = 'skipped';

/**
 * (0,0) und offensichtlich unsinnige Koordinaten aussortieren — ein Halt ohne
 * echtes Geocoding ist der bekannte Fallstrick getippter Adressen
 * (siehe `project_portal_typed_address_zero_coords`).
 */
export const isUsableCoordinate = (lat, lng) => {
  if (typeof lat !== 'number' || typeof lng !== 'number') return false;
  if (Number.isNaN(lat) || Number.isNaN(lng)) return false;
  if (Math.abs(lat) < 0.0001 && Math.abs(lng) < 0.0001) return false;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
  return true;
};

/**
 * Liest die Halte aus einem `rides`-Dokument.
 *
 * Ein unbrauchbarer Eintrag lässt die ganze Liste entfallen: eine Halte-Folge
 * mit Loch würde die Route verschieben, und „keine Halte" ist das sichere
 * Verhalten (die Fahrt sieht dann wie A→Z aus, was durch die Spiegelung
 * korrekt bleibt).
 */
export const getRideStops = (ride) => {
  const raw = ride?.stops;
  if (!Array.isArray(raw) || raw.length === 0) return [];

  const stops = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') return [];
    const lat = typeof entry.latitude === 'number' ? entry.latitude : null;
    const lng = typeof entry.longitude === 'number' ? entry.longitude : null;
    if (!isUsableCoordinate(lat, lng)) return [];
    stops.push({
      // Die Position im Array ist die Wahrheit, nicht das gespeicherte Feld.
      index: stops.length,
      latitude: lat,
      longitude: lng,
      address: (entry.address || '').trim(),
      label: (entry.label || '').trim() || null,
      type: entry.type || STOP_TYPE_DROPOFF,
      status: entry.status || STOP_STATUS_PENDING,
      completedAt: entry.completedAt || null,
      legDistanceKm: typeof entry.legDistanceKm === 'number' ? entry.legDistanceKm : null,
      legDurationMin: typeof entry.legDurationMin === 'number' ? entry.legDurationMin : null,
      externalRef: entry.externalRef || null,
    });
  }
  return stops;
};

/**
 * Anzahl Halte, ohne das Array zu laden — für Listen-Views (§7).
 */
export const getStopsTotal = (ride) => {
  const total = ride?.stopsTotal;
  if (typeof total === 'number' && total > 0) return total;
  return Array.isArray(ride?.stops) ? ride.stops.length : 0;
};

/** True, wenn die Fahrt echte Zwischenstopps hat (mehr als nur das Endziel). */
export const hasIntermediateStops = (ride) => getStopsTotal(ride) > 1;

/** Index der laufenden Etappe; fällt auf den ersten offenen Halt zurück. */
export const getCurrentStopIndex = (ride, stops = getRideStops(ride)) => {
  if (stops.length === 0) return 0;
  const stored = ride?.currentStopIndex;
  if (typeof stored === 'number' && stored >= 0 && stored < stops.length) {
    if (!isSettled(stops[stored])) return stored;
  }
  const firstOpen = stops.findIndex((s) => !isSettled(s));
  return firstOpen >= 0 ? firstOpen : stops.length - 1;
};

/** Halte vor dem Endziel (ohne Endziel) — leer bei Ein-Ziel-Fahrten. */
export const getIntermediateStops = (ride) => {
  const stops = getRideStops(ride);
  return stops.length > 1 ? stops.slice(0, -1) : [];
};

/**
 * Farben der Fahrtpunkte — wie die LiveMap-Marker (AdvancedMarker.js) und die
 * Apps (lib/utils/ride_marker_colors.dart): Abholort grün, Halt violett,
 * Ziel blau.
 */
export const RIDE_POINT_COLORS = {
  pickup: '#22C55E',
  stop: '#7C3AED',
  destination: '#1D4ED8',
};

/** Abgeschlossen oder bewusst übersprungen — beides schiebt die Etappe weiter. */
export const isSettled = (stop) =>
  stop?.status === STOP_STATUS_DONE || stop?.status === STOP_STATUS_SKIPPED;

/** Kurzform für Listen und Details: „A → B → C" (§8). */
export const routeSummary = (start, stops) =>
  [
    ...(start && start.trim() ? [start.trim()] : []),
    ...stops.filter((s) => s.address).map((s) => s.address),
  ].join(' → ');

/**
 * Baut die Fahrt-Felder aus einer Halte-Liste — **inklusive der Spiegelung des
 * letzten Halts** in `destination*` (§2.1). Der einzige Weg, auf dem das
 * Portal Halte schreiben soll: So kann die Spiegelung nicht vergessen werden.
 *
 * `stops` hier sind die Zwischenstopps OHNE Endziel; das Endziel kommt aus
 * `destination` und wird als letzter Halt angehängt.
 *
 * Ohne Zwischenstopps werden die Halte-Felder ausdrücklich auf `null` gesetzt
 * statt weggelassen: Beim Bearbeiten einer Fahrt muss das Entfernen des
 * letzten Zwischenstopps die Halte auch wirklich löschen.
 */
export const buildStopFields = ({
  intermediateStops = [],
  destinationAddress,
  destinationLocation,
  legs = null,
}) => {
  const usable = intermediateStops.filter(
    (s) => s.address && s.address.trim() && isUsableCoordinate(s.latitude, s.longitude),
  );

  if (usable.length === 0) {
    return { stops: null, stopsTotal: null, currentStopIndex: null };
  }

  const chain = [
    ...usable,
    {
      address: (destinationAddress || '').trim(),
      latitude: destinationLocation?.latitude,
      longitude: destinationLocation?.longitude,
    },
  ];

  // Etappenwerte nur übernehmen, wenn ihre Anzahl zur Halte-Folge passt — eine
  // verschobene Zuordnung wäre schlimmer als gar keine, weil sie in die
  // Abrechnung einginge.
  const legsUsable = Array.isArray(legs) && legs.length === chain.length;

  const stops = chain.map((s, i) => ({
    index: i,
    latitude: s.latitude,
    longitude: s.longitude,
    address: (s.address || '').trim(),
    type: STOP_TYPE_DROPOFF,
    status: STOP_STATUS_PENDING,
    ...(s.label ? { label: s.label } : {}),
    ...(legsUsable ? { legDistanceKm: legs[i].distanceKm, legDurationMin: legs[i].durationMin } : {}),
  }));

  const last = stops[stops.length - 1];
  return {
    stops,
    stopsTotal: stops.length,
    currentStopIndex: 0,
    // Spiegelung: destination* == letzter Halt.
    destination: last.address,
    destinationLatitude: last.latitude,
    destinationLongitude: last.longitude,
  };
};
